import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';

import SidebarToggle from './SidebarToggle';
import LayersTabsConnected from '../containers/LayersTabsConnected';
import LayersListConnected from '../containers/LayersListConnected';
import Instructions from './Instructions';
import Footer from './Footer';

/**
 * Class that houses the Sidebar with Layers Tabs and Layers List
 * Uses component state & refs to child components to programatically set the height of Layers List
 * Tracks whether or not the Instructions component's collapsable container is open
 * Uses a React Animation Add-on to smoothly expand / collapse the entire Sidebar
 */
class Sidebar extends Component {
  static propTypes = {
    appHeight: PropTypes.number.isRequired,
    isOpen: PropTypes.bool.isRequired,
    openLightbox: PropTypes.func.isRequired,
    expandSidebar: PropTypes.func.isRequired,
    collapseSidebar: PropTypes.func.isRequired,
  };

  constructor() {
    super();

    // track Instructions height in state, so that when it changes we can re-render;
    // which will update the height of LayersList
    // track whether or not the instructions collapsable container is opened here, so that if the
    // sidebar is expanded and collapsed the instructions stays opened / closed
    this.state = {
      instructionsHeight: null,
      instructionsOpened: true,
    };

    // ref to this component
    this.sidebar = null;

    // callback to pass to Instructions' collapse that will be invoked when its finished animating
    this.updateInstructionsHeight = this.updateInstructionsHeight.bind(this);

    // refs to Footer & LayersTab
    this.footer = null;
    this.layersTab = null;

    // function to pass to children to get their ref to store above
    this.getRef = this.getRef.bind(this);
    this.toggleInstructions = this.toggleInstructions.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      // if the Sidebar was previously collapsed and is now open,
      // force a re-render, otherwise the LayersList height won't be set correctly
      this.forceUpdate();
    }
  }

  getRef(ref, type) {
    // we can't just put a ref on the child component instance (that would be too easy!)
    // instead, we have to pass the child component a callback that returns its ref.
    if (type === 'footer') this.footer = ref;
    if (type === 'layersTab') this.layersTab = ref;
  }

  updateInstructionsHeight(ref) {
    if (ref && ref.offsetHeight !== this.state.instructionsHeight) {
      // height of Instructions component changed, invoke a re-render
      this.setState({
        instructionsHeight: ref.offsetHeight,
      });
    }
  }

  computeLayersListHeight() {
    // the LayersList height has to be set programatically as it's dependent on whether or not
    // the Instructions Panel is opened or closed. Very annoying...
    const { instructionsHeight } = this.state;
    if (this.sidebar && this.footer && this.layersTab && instructionsHeight) {
      return (
        this.sidebar.offsetHeight -
        this.layersTab.offsetHeight -
        this.footer.offsetHeight -
        instructionsHeight
      );
    }

    return null;
  }

  toggleInstructions() {
    // callback to the Instructions component's open / close button
    this.setState({
      instructionsOpened: !this.state.instructionsOpened,
    });
  }

  render() {
    const { instructionsOpened } = this.state;
    const { isOpen, expandSidebar, collapseSidebar, openLightbox } = this.props;
    // programatically calculate the height of the LayersList component
    const layersListHeight = this.computeLayersListHeight();

    // NOTE: the transition time must match that of the CSS transitions in _sidebar.scss as well as
    // _sidebar-toggle.scss in order for this to work correctly
    const transitionTime = 350;

    return (
      <div className="Sidebar">
        <SidebarToggle {...{ isOpen, expandSidebar, collapseSidebar }} />
        <ReactCSSTransitionGroup
          transitionName="expand-collapse"
          transitionEnterTimeout={transitionTime}
          transitionLeaveTimeout={transitionTime}
        >
          {isOpen ? (
            <div
              className="sidebar-container"
              key={'sidebar-container'}
              ref={_ => {
                this.sidebar = _;
              }}
            >
              <LayersTabsConnected getRef={this.getRef} />
              <Instructions
                isOpened={instructionsOpened}
                handleClick={this.toggleInstructions}
                collapseOnRest={this.updateInstructionsHeight}
              />
              <LayersListConnected height={layersListHeight} />
              <Footer getRef={this.getRef} openLightbox={openLightbox} />
            </div>
          ) : null}
        </ReactCSSTransitionGroup>
      </div>
    );
  }
}

export default Sidebar;



// WEBPACK FOOTER //
// ./src/components/Sidebar.jsx